import React, { useEffect, useMemo, useRef } from 'react'
import Image from 'next/image'
import Head from 'next/head'
import styled from '@emotion/styled'
import { gsColors as c } from '../../styles/Variables'
import { mq, addHeadingAnchors, enhanceHeadingAnchors, lazyLoadBodyImages, getBlogNavEntries } from '../../helpers'

import logger from '../../services/logger'
import { getBlogBySlug } from '../../services/strapi'
import articleSchema from '../../schemas/article.json'
import { getAuthor, DEFAULT_AUTHOR_SLUG } from '../../data/authors'
import AuthorByline from '../../components/blog/AuthorByline'
import AuthorCard from '../../components/blog/AuthorCard'
import BlogCta from '../../components/blog/BlogCta'
import BlogFooterCta from '../../components/blog/BlogFooterCta'
import BlogSummary from '../../components/blog/BlogSummary'
import BlogJumpNav, { JUMP_NAV_OFFSET } from '../../components/blog/BlogJumpNav'

export async function getServerSideProps({ params, res }) {
  logger.log('BlogPost.getServerSideProps: fetching blog post from Strapi', { slug: params.slug })

  const { ok, blog } = await getBlogBySlug(params.slug)

  // A transient Strapi failure (ok:false) must not be cached as a 404: return
  // notFound without a cache header so the next request can retry.
  if (!ok) {
    logger.error('BlogPost.getServerSideProps: Strapi fetch failed; not caching', { slug: params.slug })
    return { notFound: true }
  }

  // A genuine miss (published slug not found) is safe to serve as a 404, but we
  // still don't cache it so a later publish shows up promptly.
  if (!blog) {
    return { notFound: true }
  }

  // Blog posts change rarely — let CloudFront cache the rendered HTML.
  // Fresh for 1h, then serve stale up to 1d while revalidating in the background.
  res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=86400')

  return { props: { blog: { ...blog, slug: params.slug } } }
}

/**
 * Splits blog body HTML into segments with CTA insertion points.
 * CTAs are placed based on the number of h2/h3 headings in the content.
 * Content has already been sanitized by getBlogBySlug on the server.
 */
function splitBodyWithCtas(body) {
  if (!body) {
    return [body]
  }

  const headingRegex = /<h[23][^>]*>/gi
  const headings = body.match(headingRegex) || []
  const headingCount = headings.length

  if (headingCount < 3) {
    return [body]
  }

  const parts = body.split(headingRegex)
  const tags = body.match(headingRegex) || []

  const reconstructed = [parts[0]]
  for (let i = 1; i < parts.length; i++) {
    reconstructed.push((tags[i - 1] || '') + parts[i])
  }

  let ctaPositions = []

  if (headingCount >= 6) {
    const pos1 = Math.round(reconstructed.length * 0.33)
    const pos2 = Math.round(reconstructed.length * 0.66)
    ctaPositions = [pos1, pos2]
  } else {
    const pos1 = Math.round(reconstructed.length * 0.5)
    ctaPositions = [pos1]
  }

  const result = []
  for (let i = 0; i < reconstructed.length; i++) {
    result.push({ type: 'html', content: reconstructed[i] })
    if (ctaPositions.includes(i + 1)) {
      result.push({ type: 'cta' })
    }
  }

  return result
}

export default function BlogPost({ blog: { slug, title, publish_date, body, summary, featured_images, seo, category, subheadline } }) {
  const author = getAuthor(DEFAULT_AUTHOR_SLUG)

  const blogArticleSchema = {
    ...articleSchema,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://glamsquad.com/blog/${slug}`
    },
    headline: seo?.metaTitle || title,
    description: seo?.metaDescription || '',
    image: featured_images?.[0]?.url || '',
    datePublished: publish_date || '',
    dateModified: publish_date || '',
    keywords: seo?.keywords || '',
    wordCount: body ? body.replace(/<[^>]*>/g, '').split(/\s+/).length : 0,
    url: `https://glamsquad.com/blog/${slug}`,
    author: {
      "@type": "Person",
      "name": author.name,
      "url": `https://glamsquad.com/author/${author.slug}`,
      "jobTitle": author.title,
      "worksFor": {
        "@type": "Organization",
        "name": "Glamsquad"
      }
    }
  }

  const anchoredBody = lazyLoadBodyImages(addHeadingAnchors(body))
  const navEntries = useMemo(() => getBlogNavEntries(anchoredBody), [anchoredBody])
  const bodySegments = splitBodyWithCtas(anchoredBody)
  const schemaScript = JSON.stringify(blogArticleSchema)

  const contentRef = useRef(null)
  useEffect(() => {
    if (!contentRef.current) {
      return undefined
    }
    const baseUrl = `${window.location.origin}${window.location.pathname}`
    return enhanceHeadingAnchors(contentRef.current, baseUrl)
  }, [slug])

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="title" content={seo?.metaTitle || title} />
        <meta name="description" content={seo?.metaDescription || ''} />
        <link rel="canonical" href={`https://glamsquad.com/blog/${slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seo?.metaTitle || title} />
        <meta property="og:description" content={seo?.metaDescription || ''} />
        <meta property="og:url" content={`https://glamsquad.com/blog/${slug}`} />
        {featured_images?.[0]?.url && (
          <meta property="og:image" content={featured_images[0].url} />
        )}
        {seo?.shareImage?.url && (
          <meta property="og:image" content={seo.shareImage.url} />
        )}
        <meta property="article:author" content={`https://glamsquad.com/author/${author.slug}`} />
        <script type="application/ld+json">{schemaScript}</script>
      </Head>
      <PostWrapper>
        <BlogJumpNav entries={navEntries} />
        <PostHero
          {...{
            title,
            featured_images,
            publish_date,
            author,
            category,
            subheadline
          }}
        />
        <PostContentWrapper>
          <div className='content' ref={contentRef}>
            <BlogSummary summary={summary} />
            {Array.isArray(bodySegments) && bodySegments[0]?.type ? (
              bodySegments.map((segment, i) =>
                segment.type === 'cta' ? (
                  <BlogCta key={`cta-${i}`} />
                ) : (
                  <div
                    key={`content-${i}`}
                    className='injected'
                    // Sanitized by getBlogBySlug before SSR and serialization.
                    dangerouslySetInnerHTML={{ __html: segment.content }}
                  />
                )
              )
            ) : (
              // Fallback: render the sanitized body as a single block.
              // Use the anchored body so copy-link buttons work on posts with <3 headings.
              <div className='injected' dangerouslySetInnerHTML={{ __html: anchoredBody }} />
            )}
          </div>
        </PostContentWrapper>
        <PostBottomWrapper>
          <BlogFooterCta />
          <AuthorCard author={author} />
        </PostBottomWrapper>
      </PostWrapper>
    </>
  )
}
const PostContentWrapper = styled.div`
  margin-top: 60px;
  display: flex;
  justify-content: center;

  ${mq('max', '800px')} {
    margin-top: 40px;
  }

  .injected {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    width: 100%;
    p, span {
      margin: 0;
      padding: 0;
      line-height: 138% !important;
      color: black;
      font-family: Euclid;
      font-size: 20px;
      font-style: normal;
      font-weight: 400;
    }


    img {
      max-width: 100%;
      height: auto;
    }

    h2, h3 {
      scroll-margin-top: calc(${JUMP_NAV_OFFSET}px + var(--klaviyo-banner-offset, 0px));
      position: relative;
    }

    .anchor-link {
      position: absolute;
      left: -1.6em;
      top: 0.15em;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 4px;
      border: none;
      background: none;
      cursor: pointer;
      color: #9a9a9a;
      opacity: 0;
      transition: opacity 0.15s ease, color 0.15s ease;

      svg {
        width: 18px;
        height: 18px;
        display: block;
      }

      &:hover {
        color: #000;
      }
    }

    h2:hover .anchor-link,
    h3:hover .anchor-link,
    .anchor-link:focus-visible {
      opacity: 1;
    }

    .anchor-tooltip {
      position: absolute;
      left: 50%;
      bottom: calc(100% + 6px);
      transform: translateX(-50%);
      background: #000;
      color: #fff;
      font-family: Euclid;
      font-size: 12px;
      line-height: 1;
      padding: 4px 8px;
      border-radius: 4px;
      white-space: nowrap;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.15s ease;
    }

    .anchor-link.is-copied .anchor-tooltip {
      opacity: 1;
    }

    ${mq('max', '800px')} {
      .anchor-link {
        position: relative;
        left: auto;
        top: auto;
        opacity: 1;
        margin-right: 6px;
        padding: 0 4px 0 0;
        vertical-align: middle;
      }

      .anchor-tooltip {
        left: 0;
        transform: none;
      }
    }
  }

  .content {
    max-width: 750px;
    margin: 16px;

    ${mq('max', '800px')} {
      text-align: left;
    }
  }

`

const PostWrapper = styled.div`
  width: 100vw;
  overflow-x: clip;
  max-width: 1660px;
  padding-bottom: 60px;
`

const PostBottomWrapper = styled.div`
  max-width: 750px;
  margin: 0 auto;
  padding: 0 16px;
`

const PostHeroWrapper = styled.div`
  display: flex;
  min-height: 400px;
  overflow: hidden;
  background-color: #f9f6f1;
  ${mq('max', '800px')} {
    flex-direction: column;
    height: max-content;
  }

  .image {
    position: relative;
    flex: 1;
    ${mq('max', '800px')} {
      aspect-ratio: 16 / 9;
    }
  }

  .info {
    flex: 1;
    align-content: center;
    min-width: 0;
    padding: 40px 88px;
    ${mq('max', '800px')} {
      order: 1;
      padding: 40px 16px;
    }

    h1 {
      padding: 0;
      margin: 0;
      color: var(--Black, #000);
      font-family: Garnett;
      font-size: 42px;
      font-style: normal;
      font-weight: 750;
      line-height: 140%;
      letter-spacing: 0.282px;

      ${mq('max', '800px')} {
        font-size: 24px;
      }
    }

    p {
      color: var(--Dark-grey, #545454);
      font-family: Euclid;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 138%;
      letter-spacing: 0.16px;

      ${mq('max', '800px')} {
        font-size: 14px;
      }
    }

    p.eyebrow {
      margin: 0 0 12px;
      color: ${c.cobalt};
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }

    p.subheadline {
      margin: 12px 0 0;
      color: ${c.black};
      font-size: 20px;
      line-height: 150%;

      ${mq('max', '800px')} {
        font-size: 16px;
      }
    }
  }
`

const PostHero = ({ title, featured_images, publish_date, author, category, subheadline }) => {
  return (
    <PostHeroWrapper>
      <div className='info'>
        {category?.name && <p className='eyebrow'>{category.name}</p>}
        <h1>{title}</h1>
        {subheadline?.trim() && <p className='subheadline'>{subheadline}</p>}
        {author && <AuthorByline author={author} publishDate={publish_date} />}
      </div>

      {featured_images?.[0]?.url && (
        <div className='image'>
          <Image
            src={featured_images[0].url}
            alt={title}
            fill
            sizes='(max-width: 800px) 100vw, 50vw'
            priority
            style={{ objectFit: 'cover' }}
            key={featured_images[0].url}
          />
        </div>
      )}
    </PostHeroWrapper>
  )
}
